// vpsi-site/src/hooks/useSensorUpdate.js
import { useReducer, useRef, useCallback } from 'react';
import { initialState, update } from '../services/firmware/updateMachine';
import { decideUpdate } from '../services/firmware/updatePolicy';
import { canFlash } from '../services/firmware/capabilities';

const isNoPort = (err) => err && (err.name === 'NotFoundError' || /No port selected/i.test(err.message || ''));

export default function useSensorUpdate({ model, serial, espWebTools, firmwareApi, nav = navigator }) {
  const [state, dispatch] = useReducer(update, initialState);
  const stateRef = useRef(state);
  stateRef.current = state;

  const verify = useCallback(async () => {
    await serial.release().catch(() => {});
    const ports = await nav.serial.getPorts();
    for (const port of ports) {
      try {
        await serial.connect(port);
        const version = await serial.readVersion();
        dispatch({ type: 'VERIFIED', toVersion: version });
        firmwareApi.reportOutcome({ outcome: 'done', method: stateRef.current.method, fromVersion: stateRef.current.fromVersion, toVersion: version });
        return;
      } catch (_) { /* try next port */ }
    }
    firmwareApi.reportOutcome({ outcome: 'error', method: stateRef.current.method, fromVersion: stateRef.current.fromVersion, toVersion: stateRef.current.toVersion, failureReason: 'verify-reconnect-failed' });
    dispatch({ type: 'ERROR', step: 'verifying', reason: 'Could not reconnect to the sensor.', fatal: false });
  }, [nav, serial, firmwareApi]);

  const start = useCallback(async () => {
    if (!canFlash(nav)) { dispatch({ type: 'GATE_BLOCKED' }); return; }
    dispatch({ type: 'GATE_OK' });
    try {
      const port = await nav.serial.requestPort();
      await serial.connect(port);
      const { firmware, protocolVersion } = await serial.getDeviceState();
      const latestStable = await firmwareApi.getLatestStable(model);
      const decision = decideUpdate({ protocolVersion, currentVersion: firmware, latestStable });
      dispatch({ type: 'DETECTED', method: decision.method, fromVersion: firmware, toVersion: latestStable, needsUpdate: decision.needsUpdate });
      if (decision.method === 'usb') {
        // USB flashing (ESP Web Tools) needs exclusive access to the serial
        // port, and the install-button click is a synchronous user gesture that
        // cannot await a release. Free our detect-time port NOW so the browser
        // flasher can open it when the user confirms — otherwise the flash fails
        // immediately with a busy port.
        await serial.release().catch(() => {});
      }
    } catch (err) {
      if (isNoPort(err)) { dispatch({ type: 'DETECT_NO_PORT' }); return; }
      dispatch({ type: 'ERROR', step: 'detecting', reason: 'Could not read your sensor. Check the USB cable and try again.', fatal: false });
    }
  }, [nav, serial, firmwareApi, model]);

  const confirm = useCallback(async () => {
    const { method, toVersion, fromVersion } = stateRef.current;
    dispatch({ type: 'CONFIRM' });
    try {
      if (method === 'ota') {
        const bin = await firmwareApi.downloadBin(model, toVersion);
        await serial.flashOta(bin, (p) => dispatch({ type: 'PROGRESS', progress: p }));
      } else {
        await serial.release();
        await espWebTools.flash();
      }
      dispatch({ type: 'FLASH_DONE' });
      await verify();
    } catch (err) {
      firmwareApi.reportOutcome({ outcome: 'error', method, fromVersion, toVersion, failureReason: err.message });
      dispatch({ type: 'ERROR', step: 'updating', reason: err.message || 'The update did not finish.', fatal: false });
    }
  }, [serial, espWebTools, firmwareApi, model, verify]);

  const retry = useCallback(async () => {
    const step = stateRef.current.step;
    const failedStep = stateRef.current.error?.step;
    dispatch({ type: 'RETRY' });
    if (failedStep === 'detecting' || step === 'driversHelp') await start();
    else if (failedStep === 'updating') await confirm();
    else if (failedStep === 'verifying') await verify();
  }, [start, confirm, verify]);

  const cancel = useCallback(async () => {
    await serial.release().catch(() => {});
    dispatch({ type: 'CANCEL' });
  }, [serial]);

  return { state, start, confirm, retry, cancel };
}
